import type { TransactionStatus } from "@/types/transaction";

/**
 * Calculate the transaction status based on outstanding balance and due date
 * 
 * Status Logic:
 * - Outstanding: Has outstanding balance AND due date has not passed
 * - Completed: No outstanding balance (fully allocated)
 * - Overdue: Has outstanding balance AND due date has passed
 * 
 * @param outstandingAmount - The remaining balance on the transaction
 * @param dueDate - The due date of the transaction (optional)
 * @returns The calculated transaction status
 */
export function calculateTransactionStatus(
  outstandingAmount: number | undefined,
  dueDate: string | undefined
): TransactionStatus {
  // If no outstanding amount or it's 0, transaction is completed
  if (outstandingAmount === undefined || outstandingAmount === 0) {
    return "completed";
  }

  // If there's an outstanding balance
  if (outstandingAmount > 0) {
    // Check if there's a due date
    if (dueDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      
      const dueDateObj = new Date(dueDate);
      dueDateObj.setHours(0, 0, 0, 0);

      // If due date has passed, it's overdue
      if (dueDateObj < today) {
        return "overdue";
      }
    }

    // Has outstanding balance but due date hasn't passed (or no due date)
    return "outstanding";
  }

  // Default to completed
  return "completed";
}
